import React, { useMemo } from 'react';
import UserNameForm from './UserNameForm';
import { useProfilePage } from './useProfilePage';
import ChangePasswordForm from './ChangePassword';
import { Avatar, Button, Col, FormModal, Popconfirm, Row, Typography, Upload } from 'components';
import { DeleteIcon, EditIcon, ResetPassIcon } from 'components/common/icons';
import styles from './styles.module.scss';
import { useDepartmentsListQuery } from 'api';

export const UserProfilePage = React.memo(
  ({ storageUserIdentifier = 'userId' }: { storageUserIdentifier?: string }) => {
    const { data: departmentsMapping = [] } = useDepartmentsListQuery();
    const {
      form,
      contextHolder,
      userDetails,
      editModalOpened,
      handleUserUpdate,
      changePassOpened,
      changePassLoading,
      changePasswordForm,
      handleAvatarDelete,
      handleAvatarChange,
      handleChangePassword,
      setChangePassOpened,
      setEditModalOpened,
    } = useProfilePage({ storageUserIdentifier });
    const userDetailsList = useMemo(
      () => [
        { label: 'Name/Surname', value: `${userDetails.firstName} ${userDetails.lastName}`, editable: true },
        { label: 'Email', value: userDetails.email },
        {
          label: 'Department',
          value: departmentsMapping?.find((_department: string) => _department === userDetails.department) || '',
        },
        { label: 'Title', value: userDetails.title },
      ],
      [userDetails, departmentsMapping]
    );

    return (
      <Row justify={'center'}>
        {contextHolder}
        <Col flex={'680px'} className={styles.profile}>
          <Row justify={'center'}>
            <Col style={{ marginBottom: 50 }}>
              <Typography.Text className={styles.title}>Profile Page</Typography.Text>
            </Col>
          </Row>
          <Row>
            <Col flex={'120px'}>
              <Avatar src={userDetails?.files?.AVATAR} className={styles.avatar} size="large">
                {userDetails?.files?.AVATAR || (
                  <>
                    {userDetails.firstName?.charAt(0)}
                    {userDetails.lastName?.charAt(0)}
                  </>
                )}
              </Avatar>
              {userDetails?.files?.AVATAR && (
                <Popconfirm
                  title="Are you sure you want to delete Profile Picture?"
                  onConfirm={handleAvatarDelete}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button shape="circle" icon={<DeleteIcon />} size="small" className={styles.avatar_delete_btn} />
                </Popconfirm>
              )}
            </Col>
            <Col flex={'460px'}>
              <Row justify={'start'} className={styles.content} gutter={[0, 32]}>
                <Col span={24}>
                  <Typography.Text ellipsis={{ tooltip: true }} className={styles.label}>
                    {userDetails.firstName} {userDetails.lastName}
                  </Typography.Text>
                  <Typography className={styles.description}>{userDetails.title}</Typography>
                  <Upload
                    showUploadList={false}
                    beforeUpload={() => false} // Prevent automatic upload
                    onChange={handleAvatarChange}
                    accept={'.jpg,.jpeg,.png'}
                  >
                    <Button className={styles.btn_primary} variant={'outlined'}>
                      Upload Image
                    </Button>
                  </Upload>
                </Col>
              </Row>
            </Col>
            <Col style={{ margin: '32px 0' }} span={24}>
              <hr />
            </Col>
            <Col span={24}>
              <Row justify={'start'} className={styles.content} gutter={[0, 32]}>
                <Col span={24}>
                  <Typography className={styles.label}>User Details</Typography>
                </Col>
              </Row>
              {userDetailsList.map(({ label, value, editable }) => (
                <Row wrap={false} key={label} justify={'space-between'} className={styles.content} gutter={[0, 32]}>
                  <Col flex={'130px'}>
                    <Typography style={{ width: 120 }} className={styles.details}>
                      {label}
                    </Typography>
                  </Col>
                  <Col flex={'auto'} style={{ textAlign: 'right' }}>
                    {editable ? (
                      <>
                        <Typography.Text className={styles.details} ellipsis={{ tooltip: true }}>
                          {value}
                        </Typography.Text>
                        <span onClick={() => setEditModalOpened(true)}>
                          <EditIcon />
                        </span>
                      </>
                    ) : (
                      <Typography.Text className={styles.details} ellipsis={{ tooltip: true }}>
                        {value}
                      </Typography.Text>
                    )}
                  </Col>
                </Row>
              ))}
            </Col>
            <Col span={24} style={{ marginTop: 44 }}>
              <Typography className={styles.label}>Change Password</Typography>
              <Typography className={styles.description}>
                {`Reset the user's password to allow them to set a new one.`}
              </Typography>
              <Button onClick={() => setChangePassOpened(true)} className={styles.btn_primary} variant={'outlined'}>
                Change Password
              </Button>
            </Col>
          </Row>
        </Col>
        <FormModal
          okText={'Change'}
          width={360}
          form={changePasswordForm}
          open={changePassOpened}
          title={''}
          isLoading={changePassLoading}
          onSubmit={handleChangePassword}
          onCancel={() => setChangePassOpened(false)}
        >
          <Row justify={'center'}>
            <Col style={{ textAlign: 'center', margin: 15 }} span={24}>
              <ResetPassIcon />
            </Col>
            <Typography className={styles.label}>Change Password</Typography>
            <Typography
              style={{ textAlign: 'center', marginTop: 12 }}
              className={styles.description}
            >{`Please create a new password`}</Typography>
          </Row>
          <ChangePasswordForm />
        </FormModal>
        <FormModal
          okText={'Save'}
          width={360}
          form={form}
          open={editModalOpened}
          title={'Update Name/Surname'}
          isLoading={changePassLoading}
          onSubmit={handleUserUpdate}
          onCancel={() => setEditModalOpened(false)}
        >
          <UserNameForm />
        </FormModal>
      </Row>
    );
  }
);
